<template>
  <div
    class="scrollable profile-settings-panel mt-6"
  >
    <a href="#" v-if="!isPersonalHomeDisabled">
      <Icon name="arrow-left" size="14"/>
      {{ $t('events_back', { events: personalHomeLabel }) }}</a>
    <form
      class="profile__form mt-6"
      @submit.prevent="saveUpdatedPhoneNumber"
    >
      <h2 class="h2">{{ $t('profile_phone_number_title') }}</h2>
      <div class="row">
        <form-group
          is-simplified
          :label="$t('profile_phone')"
          class="col-xs-12"
        >
          <ds-input
            is-simplified
            placeholder="+32 477 280 200."
            type="tel"
            v-model="updatedEmailUserObject.phone"
          />
          <span v-if="newPhoneNumberMessage">{{ newPhoneNumberMessage }}</span>
          <Checkbox
            style="margin-top: 1rem;"
            label="Share your phone number with other users"
            :model-value="showPhoneNumber"
            @update:model-value="updateShowPhoneNumber($event)"
          />
        </form-group>

        <div
          class="col-xs-12"
          style="margin-bottom: 20px"
        >
          <ds-button
            :label="$t('profile_phone_number_save')"
            variant="rounded"
            :disabled="!isPhoneNumberUnchanged"
            @click="saveUpdatedPhoneNumber"
          />
        </div>
      </div>
    </form>
    <form
      class="profile__form"
      @submit.prevent="saveUpdatedPassword"
    >
      <h2 class="h2">{{ $t('profile_password_title') }}</h2>
      <div class="row">
        <form-group
          is-simplified
          :label="$t('profile_password_current_label')"
          :errors="errors.current_password"
          class="col-xs-12"
        >
          <ds-input
            is-simplified
            type="password"
            v-model="updatedPasswordUserObject.current_password"
          />
        </form-group>

        <form-group
          is-simplified
          :label="$t('profile_password_new_label')"
          :errors="errors.password"
          class="col-xs-12 col-sm-6"
        >
          <ds-input
            is-simplified
            type="password"
            v-model="updatedPasswordUserObject.password"
            :noAutocomplete="true"
          />
        </form-group>

        <form-group
          is-simplified
          :label="$t('profile_password_confirm_label')"
          :errors="errors.password_confirmation"
          class="col-xs-12 col-sm-6"
        >
          <ds-input
            is-simplified
            type="password"
            v-model="updatedPasswordUserObject.password_confirmation"
          />
        </form-group>
        <div
          class="col-xs-12"
          style="margin-bottom: 20px"
        >
          <ds-button
            :label="$t('profile_edit_password_save')"
            variant="rounded"
            :disabled="!isEditingPassword"
            @click="saveUpdatedPassword"
          />
        </div>
      </div>
    </form>
    <form
      class="profile__form"
      @submit.prevent="saveUpdatedEmailAddress"
    >
      <h2 class="h2">{{ $t('profile_email_title') }}</h2>
      <div class="row">
        <form-group
          is-simplified
          :label="$t('profile_email_label')"
          :errors="errors.email"
          class="col-xs-12"
        >
          <ds-input
            is-simplified
            v-model="updatedEmailUserObject.email"
            disabled
          />
        </form-group>

        <form-group
          is-simplified
          :label="$t('profile_update_email_label')"
          :errors="newEmailErrors.newEmail"
          class="col-xs-12"
        >
          <ds-input
            is-simplified
            v-model="newEmail"
          />
          <span v-if="newEmailMessage">{{ newEmailMessage }}</span>
        </form-group>
        <div
          class="col-xs-12"
          style="margin-bottom: 20px"
        >
          <ds-button
            :label="$t('profile_edit_email_save')"
            variant="rounded"
            :disabled="!isEditingEmail || !validNewEmail"
            @click="saveUpdatedEmailAddress"
          />
        </div>
      </div>
    </form>

    <form
      class="profile__form"
      style="margin-bottom: 1rem;"
    >
      <h2 class="h2">{{ $t('user_profile_leave_ecosystem') }}</h2>
      <div class="row">
        <div class="col-xs-12">
          <ds-button
            variant="rounded"
            size="small"
            icon="trash"
            @click="displayLeaveEcosystem"
            :label="$t('user_profile_leave_ecosystem_button_label')"
          />
        </div>
      </div>
    </form>
  </div>
</template>

<script>
  import OnboardingMixin from '../../util/OnboardingMixin.js'
  import Tabs from '../Tabs/Tabs.vue'
  import Avatar from '../Avatar/Avatar.vue'
  import TranslationsMixin from '../../util/TranslationsMixin.js'
  import Dropdown from '../Dropdown/Dropdown.vue'
  import UiMixin from '../../util/UiMixin.js'
  import IntroJsMixin from '../../util/IntroJsMixin.js'

  import ToggleButton from '../Form/ToggleButton.vue'
  import AvatarNew from '@/components/Avatar/AvatarNew.vue'
  import { changeEmailRequest, updatePhoneNumber, updateProfile } from '@/api/user'
  import CommunityMemberCard from '@/components/Communities/CommunityMemberCard.vue'
  import ActorMiniCard from '@/components/ActorMiniCard/ActorMiniCard.vue'
  import ChallengeCardNew from '@/components/Challenges/ChallengeCardNew.vue'
  import AnnouncementCard from '@/components/Simplified/AnnouncementCard.vue'
  import ActorInfoCard from '@/components/ActorInfo/ActorInfoCard.vue'
  import SimplifiedImageCard from '@/components/Connection/SimplifiedImageCard.vue'
  import ProfileCardsSection from '@/components/Profile/ProfileCardsSection.vue'
  import Checkbox from '@/components/Form/Checkbox.vue'
  import { trackHeapEvent } from '@/util/analytics'
  import { prepareUserForEditing } from '@/util/user-settings'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids'
  import { MUTATION_TYPES as USER_MUTATION_TYPES } from '@/store/modules/user-helpers'
  import isEqual from 'lodash/isEqual'

  export default {
    name: 'PasswordAndSecurity',
    data () {
      return {
        componentKey: 1,
        updatingAllowPersonalisedContentOption: false,
        updatedPasswordUserObject: this.currentUser(),
        updatedEmailUserObject: this.currentUser(),
        user: this.currentUser(),
        newEmail: null,
        newEmailMessage: null,
        newEmailErrors: {},
        saving: false,
        newPhoneNumber: null,
        newPhoneNumberMessage: null,
        initialPhoneNumber: '',
        errors: {},
      }
    },
    computed: {
      isPersonalHomeDisabled () {
        return !!this.$store.state.config.personalHomeDisabled
      },
      personalHomeLabel () {
        return this.$t('profile_settings_personal_home')
      },
      showPhoneNumber () {
        return this.user.phone_published === 1;
      },
      isPhoneNumberUnchanged () {
        return this.updatedEmailUserObject.phone !== this.initialPhoneNumber
      },
      profile () {
        return this.$store.state.user.profile
      },
      isEditingPassword () {
        return !isEqual(this.updatedPasswordUserObject, this.currentUser())
      },
      isEditingEmail () {
        return !isEqual(this.updatedEmailUserObject, this.currentUser()) || this.newEmail
      },
      validNewEmail () {
        if (!this.newEmail || this.newEmail === this.updatedEmailUserObject.email) {
          return false
        }

        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        return re.test(String(this.newEmail).toLowerCase())
      },
    },
    methods: {
      saveUpdatedEmailAddress () {
        if (!this.isEditingEmail || !this.validNewEmail) {
          return
        }
        this.newEmailErrors = {}
        this.newEmailMessage = ''

        changeEmailRequest(this.newEmail)
          .then(response => {
            this.newEmailMessage = 'An email has been sent to ' + this.newEmail + ' in order to complete the update.'
            this.newEmail = ''
          })
          .catch(errors => {
            this.newEmailErrors = errors
          })
      },
      updateShowPhoneNumber (newValue) {
        updatePhoneNumber({ phone: this.user.phone, phone_published: newValue ? 1 : 0 })
          .then(data => {
            trackHeapEvent('updatePhoneNumber.success')
          })
          .catch((errors) => {
            console.log(errors)
          })
      },
      async saveUpdatedPassword () {
        if (!this.isEditingPassword) {
          return
        }

        updateProfile({
          current_password: this.updatedPasswordUserObject.current_password,
          password: this.updatedPasswordUserObject.password,
          password_confirmation: this.updatedPasswordUserObject.password_confirmation,
        })
          .then(data => {
            this.errors = {}
            this.$store.commit(USER_MUTATION_TYPES.UPDATE_PROFILE, data)
            setTimeout(() => {
              this.resetUserObjects()
            })
            trackHeapEvent('changePassword.success')
          })
          .catch(errors => {
            this.errors = errors
            trackHeapEvent('changePassword.failure', { errorMessage: JSON.stringify(errors) })
          })
      },
      displayLeaveEcosystem () {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.USER_LEAVE_ECOSYSTEM)
      },
      currentUser () {
        const profile = this.$store.state.user.profile
        return prepareUserForEditing(profile)
      },
      saveUpdatedPhoneNumber () {
        updatePhoneNumber({ phone: this.updatedEmailUserObject.phone, phone_published: true })
          .then(response => {
            this.newPhoneNumberMessage = 'Your phone number is successfully updated'
            trackHeapEvent('saveUpdatedPhoneNumber.success')
          })
          .catch(errors => {
            console.log(errors)
          })
      },
    },
    async mounted () {
    },
    beforeUnmount () {
    },
    mixins: [OnboardingMixin, TranslationsMixin, UiMixin, IntroJsMixin],
    components: {
      Checkbox,
      ProfileCardsSection,
      SimplifiedImageCard,
      ActorInfoCard,
      AnnouncementCard,
      ChallengeCardNew,
      ActorMiniCard,
      CommunityMemberCard,
      AvatarNew,
      Tabs,
      Avatar,
      Dropdown,
      ToggleButton
    },
  }
</script>

<style lang="scss" scoped>
  @import "../../../scss/_variables.scss";

  .ph__allow-personalisation-container {
    display: flex;
    height: 3rem;
    align-items: center;
    justify-content: center;
  }

  .ph__card-container {
    @media (max-width: $screen-lg) {
      margin-bottom: 20px;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .ph__card {
    box-shadow: $simplified-box-shadow-config;
    border-radius: $default-border-radius;
    position: relative;
    margin-bottom: 20px;

    &.ph__action-card {
      margin-bottom: 0;
      padding: 10px;
      display: flex;
      flex-direction: column;

      &:last-child {
        margin-right: 0;
      }

      .ph__action-card-image {
        background-repeat: no-repeat;
        background-size: cover;
        background-position: center center;
        width: 100%;
      }
    }

    &.with-tabs {
      background-color: var(--primary-extra-lightest);
    }
  }

  .ph__welcome-text {
    padding: 20px 70px;
  }

  h4.h4 {
    margin-bottom: 10px;
    text-align: center;
  }

  p {
    margin-bottom: 20px;
    line-height: 15px;
    letter-spacing: 0.5px;
    text-align: center;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .ph__tabs-content {
    padding: 20px;
  }

  :deep(.button__label) {
    overflow-x: hidden;
    display: block;
    text-overflow: ellipsis;
    text-align: center;
  }

  .ph__card-title {
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    height: 16px;
    font-size: 14px;
    text-overflow: ellipsis;
    margin: 10px 0;
  }

  .ph__card-description {
    $lineHeight: 15px;
    $nrOfLines: 3;
    display: -webkit-box;
    width: 100%;
    height: $nrOfLines*$lineHeight;
    -webkit-line-clamp: $nrOfLines;
    line-height: $lineHeight;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
    margin-bottom: 15px;

    &.announcement-description {
      $lineHeight: 15px;
      $nrOfLines: 6;
      height: $nrOfLines*$lineHeight;
      -webkit-line-clamp: $nrOfLines;
    }

    &.resource-description {
      $lineHeight: 15px;
      $nrOfLines: 6;
      height: $nrOfLines*$lineHeight;
      -webkit-line-clamp: $nrOfLines;
    }

    p {
      text-align: left;
    }
  }

  hr {
    flex-grow: 1;
    border: 1px solid var(--primary-extra-lightest);
    height: 0;
  }

  .ph__relevant-yes-no {
    margin: 10px 0;
    font-size: 12px;

    span {
      line-height: 10px;
      border: 1px solid var(--primary);
      border-radius: $default-border-radius-narrow;
      margin: 5px 5px 10px 0;
      cursor: pointer;
      padding: 5px 10px;
      display: inline-block;

      &.is-active {
        background: var(--primary-extra-lightest);
      }
    }
  }

  .ph__announcement-creator {
    display: flex;

    .ph__announcement-creator-name {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow-x: hidden;
      line-height: 22px;
    }
  }

  .ph__resource-title {
    font-size: 14px;
    font-weight: 500;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  :deep(.multiselect.multiselect--datascouts) {
    .multiselect__tags {
      padding: 0;
      border: 0
    }

    &.multiselect--active {
      .multiselect__tags {
        box-shadow: none !important; // important to override important in _focus-state.scss
      }
    }

    .multiselect__content-wrapper {
      display: none;
    }

    .multiselect__placeholder {
      display: none;
    }
  }

  .ph__resource-description {
    $lineHeight: 16px;
    $nrOfLines: 2;
    margin-bottom: 10px;
    margin-top: 5px;
    $lineHeight: 16px;
    $nrOfLines: 6;
    display: -webkit-box;
    width: 100%;
    height: $nrOfLines*$lineHeight;
    -webkit-line-clamp: $nrOfLines;
    line-height: $lineHeight;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
  }
</style>
